import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AppleNewsSection,
  ArticleCollection,
  ArticleTag,
  ArticleTopic,
  PaginatedResponse,
} from "@/api/types";
import { getApiErrorMessage } from "@/lib/api-error";
import { baseKey } from "@/lib/base-key";
import type { QueryComponentProps } from "@/lib/component-query";
import { log } from "@/lib/log";
import {
  archiveArticle,
  archivePrint,
  backToDraftArticle,
  cancelPublish,
  createArticle,
  createJournalist,
  createMediaItem,
  deleteArticle,
  type GetArticlesParams,
  type GetRecentGoogleDocsParams,
  getAllArticles,
  getAppleNewsSections,
  getArticles,
  getCollections,
  type getCollectionsParams,
  getGridCollections,
  getGridImage,
  getGridImages,
  getJournalists,
  type getJournalistsParams,
  getMediaItems,
  type getMediaItemsParams,
  getOldArticles,
  getPresenceBulk,
  getPuzzleLookup,
  getPuzzleTypes,
  getRecentGoogleDocs,
  getTags,
  type getTagsParams,
  getTopics,
  type getTopicsParams,
  getWoodwingBooks,
  getYoutubeVideos,
  importGoogleDoc,
  purgeArticleCache,
  republishArticle,
  scheduleArticle,
  schedulePrint,
  sendToPrint,
  unpublishArticle,
  updateMediaItem,
} from "./services";

export const $user = baseKey(["user"]);
export const $google = $user.extend(["google"]);

export const $articles = $user.extend(["articles"]);
export const $articlesRecords = $articles.extend(["records"]);
export const $oldArticlesRecords = $articles.extend(["wagtail-records"]);
export const $articlesTags = $articles.extend(["tags"]);
export const $articlesTopics = $articles.extend(["topics"]);
export const $articlesCollections = $articles.extend(["collections"]);
export const $articleAppleNewsSections = $articles.extend(["apple_news_sections"]);

export const $presence = $user.extend(["presence"]);

export const usePresenceBulkQuery = (articleIds: number[]) =>
  useQuery({
    queryKey: $presence.extend([articleIds]).queryKey,
    queryFn: () => getPresenceBulk(articleIds),
    enabled: articleIds.length > 0,
    staleTime: 15_000,
    refetchInterval: 15_000,
  });

export const $puzzles = $user.extend(["puzzles"]);
export const $puzzleTypes = $puzzles.extend(["types"]);

export const $$puzzleTypes = (enabled?: boolean) =>
  queryOptions({
    staleTime: 5 * 60 * 1000,
    queryKey: $puzzleTypes.queryKey,
    queryFn: ({ signal }) => getPuzzleTypes(signal),
    enabled,
  });

export const usePuzzleTypesQuery = (enabled?: boolean) => useQuery($$puzzleTypes(enabled));

export const $puzzleLookup = $puzzles.extend(["lookup"]);
export const $$puzzleLookup = (
  params: { variant: string; number: string },
  opts: { enabled?: boolean } = {},
) =>
  queryOptions({
    queryKey: $puzzleLookup.extend([params]).queryKey,
    queryFn: ({ signal }) => getPuzzleLookup(params, signal),
    enabled: opts.enabled,
  });
export const usePuzzleLookupQuery = (
  params: { variant: string; number: string },
  opts?: Parameters<typeof $$puzzleLookup>[1],
) => useQuery($$puzzleLookup(params, opts));

export const $mediaItems = $user.extend(["media-items"]);
export const $mediaItemsRecords = $mediaItems.extend(["records"]);

export const $youtube = $user.extend(["youtube"]);
export const $youtubeVideos = $youtube.extend(["videos"]);

export const $$articlesRecords = (params: GetArticlesParams, enabled?: boolean) => {
  return queryOptions({
    queryKey: $articlesRecords.extend([params]).queryKey,
    queryFn: ({ signal }) => getArticles(params, signal),
    enabled: enabled,
  });
};

// Nested under the records key so the invalidation every article mutation already does reaches the
// calendar too — archive, delete, unpublish, create and the rest need no calendar-specific line.
export const $calendarWeek = $articlesRecords.extend(["calendar-week"]);
export const $calendarDrafts = $articlesRecords.extend(["calendar-drafts"]);

export const $$calendarWeek = (params: GetArticlesParams) =>
  queryOptions({
    queryKey: $calendarWeek.extend([params]).queryKey,
    queryFn: ({ signal }) => getAllArticles({ ...params, ordering: "publish_at" }, signal),
    // Paging weeks changes the key, and a week is up to six sequential requests. Keep the last
    // week on screen rather than tearing the board down for a skeleton while the next one loads.
    placeholderData: keepPreviousData,
  });

export const useCalendarWeekQuery = (params: GetArticlesParams) => useQuery($$calendarWeek(params));

const CALENDAR_DRAFTS_PAGE_SIZE = 20;

export const $$calendarDrafts = (params: GetArticlesParams) =>
  infiniteQueryOptions({
    queryKey: $calendarDrafts.extend([params]).queryKey,
    queryFn: ({ pageParam, signal }) =>
      getArticles(
        {
          ...params,
          status: "web-draft",
          ordering: "-created_at",
          limit: CALENDAR_DRAFTS_PAGE_SIZE,
          page: pageParam,
        },
        signal,
      ),
    initialPageParam: 1,
    // The endpoint returns page 1 rather than 404 past the end, so trust `next` alone to stop.
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
  });

export const useCalendarDraftsQuery = (params: GetArticlesParams) =>
  useInfiniteQuery($$calendarDrafts(params));

export const $$mediaItemsRecords = (
  params: getMediaItemsParams,
  enabled?: boolean,
  keepPreviousData?: boolean,
) => {
  return queryOptions({
    queryKey: $mediaItemsRecords.extend([params]).queryKey,
    queryFn: ({ signal }) => getMediaItems(params, signal),
    enabled: enabled,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
  });
};

export const $$youtubeVideos = (pageToken?: string) =>
  queryOptions({
    staleTime: 5 * 60 * 1000,
    queryKey: $youtubeVideos.extend([{ pageToken: pageToken ?? "first" }]).queryKey,
    queryFn: ({ signal }) => getYoutubeVideos(pageToken, signal),
  });

export const useYoutubeVideosQuery = (pageToken?: string) => useQuery($$youtubeVideos(pageToken));

export const $grid = $user.extend(["grid"]);
export const $gridCollections = $grid.extend(["collections"]);
export const $gridImages = $grid.extend(["images"]);

export const $$gridCollections = () =>
  queryOptions({
    staleTime: 60 * 60 * 1000,
    queryKey: $gridCollections.queryKey,
    queryFn: ({ signal }) => getGridCollections(signal),
  });

export const useGridCollectionsQuery = () => useQuery($$gridCollections());

export const $$gridImages = (params: { q?: string; collection?: string; offset?: number; length?: number }) =>
  queryOptions({
    staleTime: 2 * 60 * 1000,
    queryKey: $gridImages.extend([params]).queryKey,
    queryFn: ({ signal }) => getGridImages(params, signal),
    placeholderData: (previousData) => previousData,
  });

export const useGridImagesQuery = (
  params: {
    q?: string;
    collection?: string;
    offset?: number;
    length?: number;
  },
  options?: { enabled?: boolean },
) => useQuery({ ...$$gridImages(params), enabled: options?.enabled ?? true });

export const $$gridImage = (imageId: string | undefined) =>
  queryOptions({
    staleTime: 2 * 60 * 1000,
    queryKey: $gridImages.extend([{ id: imageId }]).queryKey,
    queryFn: ({ signal }) => getGridImage(imageId as string, signal),
    enabled: !!imageId,
    // A picture the desk has since deleted shouldn't cost four round trips
    // before the picker falls back to the image we hold.
    retry: false,
  });

export const useGridImageQuery = (imageId: string | undefined) => useQuery($$gridImage(imageId));

const $journalists = $user.extend(["journalists"]);
export const $journalistsRecords = $journalists.extend(["records"]);

export const $$journalistsRecords = (
  params: getJournalistsParams,
  enabled?: boolean,
  keepPreviousData?: boolean,
) => {
  return queryOptions({
    staleTime: 5000,
    queryKey: $journalistsRecords.extend([params]).queryKey,
    queryFn: ({ signal }) => getJournalists(params, signal),
    enabled: enabled,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
  });
};

export const useJournalistsQuery = (
  params: getJournalistsParams,
  enabled?: boolean,
  keepPreviousData?: boolean,
) => {
  return useQuery($$journalistsRecords(params, enabled, keepPreviousData));
};

export const useCreateJournalistMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createJournalist,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: $journalistsRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to create journalist", e, variables);
    },
  });
};

export const $tagsRecords = $articlesTags.extend(["records"]);

export const $$tagsRecords = (params: getTagsParams, enabled?: boolean, keepPreviousData?: boolean) => {
  return queryOptions({
    staleTime: 5000,
    queryKey: $tagsRecords.extend([params]).queryKey,
    queryFn: ({ signal }) => getTags(params, signal),
    enabled: enabled,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
  });
};

export const useTagsQuery = (params: getTagsParams, enabled?: boolean, keepPreviousData?: boolean) => {
  return useQuery($$tagsRecords(params, enabled, keepPreviousData));
};

export const TagsQuery = (props: QueryComponentProps<PaginatedResponse<ArticleTag>>) => {
  const query = useTagsQuery({ id: props.id }, props.enabled, props.keepPreviousData);

  return props.children({ data: query.data, isLoading: query.isLoading, isFetching: query.isFetching });
};

export const $topicsRecords = $articlesTopics.extend(["records"]);

export const $$topicsRecords = (params: getTopicsParams, enabled?: boolean, keepPreviousData?: boolean) => {
  return queryOptions({
    staleTime: 5000,
    queryKey: $topicsRecords.extend([params]).queryKey,
    queryFn: ({ signal }) => getTopics(params, signal),
    enabled: enabled,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
  });
};

export const useTopicsQuery = (params: getTopicsParams, enabled?: boolean, keepPreviousData?: boolean) => {
  return useQuery($$topicsRecords(params, enabled, keepPreviousData));
};

export const TopicsQuery = (props: QueryComponentProps<PaginatedResponse<ArticleTopic>>) => {
  const query = useTopicsQuery({ id: props.id }, props.enabled, props.keepPreviousData);

  return props.children({ data: query.data, isLoading: query.isLoading, isFetching: query.isFetching });
};

export const $appleNewsSectionsRecords = $articleAppleNewsSections.extend(["records"]);

export const $$appleNewsSectionsRecords = (enabled?: boolean, keepPreviousData?: boolean) => {
  return queryOptions({
    staleTime: 5000,
    queryKey: $appleNewsSectionsRecords.queryKey,
    queryFn: ({ signal }) => getAppleNewsSections(signal),
    enabled: enabled,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
  });
};

export const useAppleNewsSectionsQuery = (enabled?: boolean, keepPreviousData?: boolean) => {
  return useQuery($$appleNewsSectionsRecords(enabled, keepPreviousData));
};

export const AppleNewsSectionsQuery = (
  props: QueryComponentProps<AppleNewsSection[], string>,
): React.ReactNode => {
  const query = useAppleNewsSectionsQuery(props.enabled, props.keepPreviousData);

  return props.children({ data: query.data, isLoading: query.isLoading, isFetching: query.isFetching });
};

export const $collectionsRecords = $articlesCollections.extend(["records"]);

export const $$collectionsRecords = (
  params: getCollectionsParams,
  enabled?: boolean,
  keepPreviousData?: boolean,
) => {
  return queryOptions({
    staleTime: 5000,
    queryKey: $collectionsRecords.extend([params]).queryKey,
    queryFn: ({ signal }) => getCollections(params, signal),
    enabled: enabled,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
  });
};

export const useCollectionsQuery = (
  params: getCollectionsParams,
  enabled?: boolean,
  keepPreviousData?: boolean,
) => {
  return useQuery($$collectionsRecords(params, enabled, keepPreviousData));
};

export const CollectionsQuery = (props: QueryComponentProps<PaginatedResponse<ArticleCollection>>) => {
  const query = useCollectionsQuery({ id: props.id }, props.enabled, props.keepPreviousData);

  return props.children({ data: query.data, isLoading: query.isLoading, isFetching: query.isFetching });
};

export const useArticlesQuery = (params: GetArticlesParams, enabled?: boolean) => {
  return useQuery($$articlesRecords(params, enabled));
};

// Fetches a single article's live card data (title, image, standfirst, status) by
// id via the list endpoint's id filter, so an inserted article card always
// reflects the current linked article rather than a stale snapshot.
export const useArticleCardQuery = (id: number | undefined, enabled = true) =>
  useQuery({
    staleTime: 30_000,
    queryKey: $articlesRecords.extend(["card", id ?? "none"]).queryKey,
    queryFn: async ({ signal }) => {
      const res = await getArticles({ id: id as number, limit: 1 }, signal);
      return res.results[0] ?? null;
    },
    enabled: enabled && id != null,
  });

export const useOldArticlesQuery = (params: GetArticlesParams, enabled?: boolean) => {
  return useQuery({
    queryKey: $oldArticlesRecords.extend([params]).queryKey,
    queryFn: ({ signal }) => getOldArticles(params, signal),
    enabled,
  });
};

export const useCreateArticleMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: createArticle,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
  });
};

export const useMediaItemsQuery = (
  params: getMediaItemsParams,
  enabled?: boolean,
  keepPreviousData?: boolean,
) => {
  return useQuery($$mediaItemsRecords(params, enabled, keepPreviousData));
};

export const useCreateMediaItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMediaItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: $mediaItems.queryKey });
    },
    onError: (e, variables) => {
      // Not the whole request: it carries the credit, which is usually a person's name.
      log.error("Failed to create media item", e, { title: variables.title });
    },
  });
};

export const useUpdateMediaItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMediaItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: $mediaItems.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to update media item", e, { id: variables.id });
    },
  });
};

export const useArchiveArticleMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: archiveArticle,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to archive article:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
  });
};

export const useDeleteArticleMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: deleteArticle,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to delete article", e, variables);
    },
  });
};

export const useUnpublishArticleMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: unpublishArticle,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to unpublish article:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
  });
};

export const useCancelPublishMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: cancelPublish,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to cancel publish:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
  });
};

export const useRepublishArticleMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: republishArticle,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to republish article:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
  });
};

export const usePurgeArticleCacheMutation = () => {
  return useMutation({
    mutationFn: purgeArticleCache,
    onError: (e, variables) => {
      log.error("Failed to purge article cache:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
  });
};

export const useScheduleArticleMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: scheduleArticle,
    onSettled: (_data, error) => {
      const refreshed = qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
      // On success, hold the mutation open until the active lists have refetched: the calendar
      // keeps its optimistic move until then, so the card never flashes back to its old day. A
      // failed schedule has nothing to bridge, so it reports straight away.
      return error ? undefined : refreshed;
    },
    onError: (e, variables) => {
      log.error("Failed to schedule article:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
  });
};

export const useBackToDraftArticleMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: backToDraftArticle,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error(
        "Failed to move article back to draft:",
        getApiErrorMessage(e) ?? "unknown error",
        e,
        variables,
      );
    },
  });
};

const $recentGoogleDocs = $google.extend(["recent-google-docs"]);
export const $$recentGoogleDocs = (params: GetRecentGoogleDocsParams, opts: { enabled?: boolean } = {}) => {
  return queryOptions({
    queryKey: $recentGoogleDocs.extend([params]).queryKey,
    queryFn: ({ signal }) => getRecentGoogleDocs(params, signal),
    enabled: opts.enabled,
    // Searching changes the query key, so without this the list is torn down and replaced by
    // the loader on every keystroke. Keep the previous results visible while the next load runs.
    placeholderData: (previousData) => previousData,
  });
};

export const useRecentGoogleDocsQuery = (
  params: GetRecentGoogleDocsParams,
  opts?: Parameters<typeof $$recentGoogleDocs>[1],
) => {
  return useQuery($$recentGoogleDocs(params, opts));
};

export const useImportGoogleDocMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: importGoogleDoc,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
    onError: (e, variables) => {
      log.error("Failed to import Google Doc:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
  });
};

const $woodwing = $user.extend(["woodwing"]);
export const $woodwingBooks = $woodwing.extend(["books"]);

export const useWoodwingBooksQuery = () => {
  return useQuery({
    queryKey: $woodwingBooks.queryKey,
    queryFn: ({ signal }) => getWoodwingBooks(signal),
  });
};

export const useSendCopyToPrintMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: sendToPrint,
    onError: (e, variables) => {
      log.error("Failed to send copy to print:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
  });
};

export const useArchivePrintMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: archivePrint,
    onError: (e, variables) => {
      log.error("Failed to archive print article:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
  });
};

export const useSchedulePrintMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: schedulePrint,
    onError: (e, variables) => {
      log.error("Failed to schedule print:", getApiErrorMessage(e) ?? "unknown error", e, variables);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: $articlesRecords.queryKey });
    },
  });
};
